// Deck stats modal — per-deck DATA, as opposed to deck-settings'
// per-deck CONFIG. Opened from the "niv" pill in the header (the
// pill shows a CEFR level that IS a deck-specific datum, so it's a
// natural entry point to the whole "how am I doing on this deck?"
// picture).
//
// Contents:
//   1. Stats grid (seen / correct / session / due / acquired)
//   2. Mastery bar (Leitner box histogram)
//   3. Detail by CEFR level button → levelModal, bilingual only
//
// Separated from deck-settings in its own commit because "data" and
// "settings" are conceptually distinct — users expect to TWEAK things
// in settings, READ things in stats. Mixing broke expectations.

import type { CardList } from "../config/lists";
import { t } from "../i18n";
import { byId, hide, show } from "./dom";

export type DeckStatsDeps = {
  getList: () => CardList;
  /** Render the stats grid + mastery histogram — both owned by
   *  stats-modals.ts, which writes into the #userModalStats /
   *  #userModalMastery elements that now live in this modal's body. */
  renderStats: () => void;
  renderMasteryBar: () => void;
  /** Open the CEFR level breakdown modal. Triggered by the "Détail
   *  par niveau CEFR" button; hidden on mono decks (CEFR isn't
   *  meaningful there). */
  openLevelModal: () => void;
};

export type DeckStats = {
  open: () => void;
  close: () => void;
  localize: () => void;
};

export function createDeckStats(deps: DeckStatsDeps): DeckStats {
  function localize(): void {
    const closeBtn = byId<HTMLButtonElement>("deckStatsCloseBtn");
    if (closeBtn) closeBtn.textContent = t("deck_stats_close") as string;
  }

  function renderTitle(): void {
    const titleEl = byId("deckStatsModalTitle");
    const subEl = byId("deckStatsModalSub");
    const list = deps.getList();
    const deckName = t(list.nameKey) as string;
    if (titleEl) {
      titleEl.textContent =
        (t("deck_stats_title_prefix") as string) + deckName;
    }
    if (subEl) {
      subEl.textContent = t("deck_stats_sub") as string;
    }
  }

  function syncLevelButton(): void {
    const btn = byId<HTMLButtonElement>("deckStatsLevelBtn");
    if (!btn) return;
    const isMono = deps.getList().shape === "mono";
    btn.classList.toggle("hidden", isMono);
    btn.textContent = t("deck_settings_level_btn") as string;
    btn.onclick = () => {
      // Close this modal first so the level modal is the single open
      // overlay. Reopen stats from the niv pill when done.
      hide(byId("deckStatsModal"));
      deps.openLevelModal();
    };
  }

  function open(): void {
    localize();
    renderTitle();
    deps.renderStats();
    deps.renderMasteryBar();
    syncLevelButton();
    show(byId("deckStatsModal"));
  }

  function close(): void {
    hide(byId("deckStatsModal"));
  }

  return { open, close, localize };
}

export function installDeckStatsModal(onClose: () => void): void {
  const modal = byId("deckStatsModal");
  const closeBtn = byId("deckStatsCloseBtn");
  closeBtn?.addEventListener("click", onClose);
  modal?.addEventListener("click", (e) => {
    if (e.target === modal) onClose();
  });
}
